import { Auth } from "aws-amplify";
import { LATEST_TERMS_UPDATE } from "../constants/Constant";

// 謄本購入リストを Stripe Checkout 遷移前に一時保存する sessionStorage キー
// （決済キャンセルで戻った際に PaymentCancelOverlay がここから復元する）
export const TOHON_PENDING_STORAGE_KEY = "tohonBuyListPending";

// 謄本関連 ---

// 営業時間の判定
export const getOutsideBusinessHoursMessage = (date = new Date()) => {
  // 設定ファイルから休業日を判定
  const message = IsRingiOutOfBusiness(date);
  if (message) {
    return message;
  }
  return isOutsideBusinessHours(date) ? "ただいまの時間は購入できません" : "";
};

// JSTで曜日・時刻を取得するためのフォーマッタ
const dateTimeFormatterJST = new Intl.DateTimeFormat("ja-JP", {
  timeZone: "Asia/Tokyo",
  weekday: "short", // '月', '火', ...
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

// 時間が営業時間外（JST基準）であるか判定する
const isOutsideBusinessHours = (date = new Date()) => {
  const parts = dateTimeFormatterJST.formatToParts(date);
  let hour, minute, weekday;
  for (const part of parts) {
    switch (part.type) {
      case "hour":
        hour = part.value;
        break;
      case "minute":
        minute = part.value;
        break;
      case "weekday":
        weekday = part.value;
        break;
      default:
        break;
    }
  }
  const timeString = `${hour}${minute}`;
  const dayString = weekday;

  if (dayString === "日" || dayString === "土") {
    // 日曜日か土曜日の時：休日
    return !(timeString >= "0830" && timeString < "1730");
  } else {
    // 平日
    return !(timeString >= "0830" && timeString < "2230");
  }
};

export function IsBuyTohon(
  userid,
  email,
  code,
  city,
  oaza,
  chome,
  koaza,
  yobi,
  chiban,
  isUserLoggedIn
) {
  let ret = false;
  if (userid != null && email != null && email !== "null" && isUserLoggedIn) {
    if (ISOKChiban(code, city, oaza, chome, koaza, yobi, chiban)) {
      ret = true;
    }
  }
  return ret;
}

export function ISOKChiban(code, city, oaza, chome, koaza, yobi, chiban) {
  let ret = false;
  if (
    code != null &&
    code.length === 5 &&
    Number(code.substr(0, 2)) >= 1 &&
    Number(code.substr(0, 2)) <= 47 &&
    city != null &&
    chiban != null
  ) {
    ret = true;
  } else {
    alert(
      "選択した地番は、参考データに誤りがあるため、不動産登記情報（土地）を取得できません。"
    );
  }
  return ret;
}

// 地番アイテムから表示用の名称（地番）を組み立てる
function buildChibanName(item) {
  return `${item.city ?? ""}${item.oaza ?? ""}${item.chome ?? ""}${
    item.koaza ?? ""
  }${item.yobi ?? ""}${item.chiban ?? ""}`;
}

/**
 * 謄本購入：Stripe Checkout（オーソリ／手動キャプチャ）へ同一タブ遷移する。
 * - 金額はフロントから送らない（金額は Stripe ダッシュボードの Price が権威）。
 *   フロントが送るのは地番リスト（各商品名＋属性）と個数のみ。改ざんした金額でも課金額は変わらない。
 * - success_url / cancel_url は既存サブスクと同じ方式で、現在の地図位置（window.location.hash）を
 *   付与して遷移前の表示位置を復元する。クエリ（tohon_result）は戻り先オーバーレイが検知する。
 * - 決済キャンセルで戻った際に購入リストを復元できるよう、遷移前に sessionStorage に保存する。
 * @returns {Promise<void>} 成功時は Stripe Checkout へ画面遷移する（解決前にページを離れる）
 */
export async function BuyTohon(userid, email, buyList, isUserLoggedIn) {
  // 購入可能な地番かを全件検証する
  const isAllValid = buyList.every((element) =>
    IsBuyTohon(
      userid,
      email,
      element.code,
      element.city,
      element.oaza,
      element.chome,
      element.koaza,
      element.yobi,
      element.chiban,
      isUserLoggedIn
    )
  );
  if (!isAllValid) {
    alert("エラーが発生しました。");
    throw new Error("購入処理のフラグチェックでエラー");
  }

  // 件数（1〜5件）の範囲チェック（サーバー側でも検証する）
  if (buyList.length < 1 || buyList.length > 5) {
    alert("購入できる件数は1〜5件です。");
    throw new Error("購入件数が範囲外です");
  }

  const apiUrl = process.env.REACT_APP_STRIPE_TOHON_CHECKOUT_API_URL;
  if (!apiUrl) {
    alert("決済機能が正しく設定されていません。");
    throw new Error("REACT_APP_STRIPE_TOHON_CHECKOUT_API_URL が未設定です");
  }

  // Cognito の IDトークン（サーバー側でユーザーを検証する）
  const session = await Auth.currentSession();
  const token = session.getIdToken().getJwtToken();

  // 戻り先URL（現在の地図位置 hash を付与して遷移前の表示位置を復元する。既存サブスクと同方式）
  const base = `${window.location.origin}${window.location.pathname}`;
  const success_url = `${base}?tohon_result=success${window.location.hash}`;
  const cancel_url = `${base}?tohon_result=cancel${window.location.hash}`;

  // フロントが送るのは地番リストと個数のみ（金額は送らない）
  const chiban_list = buyList.map((item) => ({
    name: buildChibanName(item),
    code: item.code,
    city: item.city,
    oaza: item.oaza,
    chome: item.chome,
    koaza: item.koaza,
    yobi: item.yobi,
    chiban: item.chiban,
  }));

  // 決済キャンセルで戻った際にリストを復元できるよう、遷移前に一時保存する
  try {
    sessionStorage.setItem(TOHON_PENDING_STORAGE_KEY, JSON.stringify(buyList));
  } catch (e) {
    // sessionStorage が使えない環境でも決済自体は継続する
    console.warn("購入リストの一時保存に失敗しました", e);
  }

  const response = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      userid,
      email,
      chiban_list,
      success_url,
      cancel_url,
    }),
  });

  if (!response.ok) {
    throw new Error(
      `Checkout セッション作成に失敗しました (HTTP ${response.status})`
    );
  }

  const { url } = await response.json();
  if (!url) {
    throw new Error("決済URLの取得に失敗しました");
  }

  // 同一タブで Stripe Checkout へ遷移（新規タブは開かない）
  window.location.href = url;
}

// -----------

//3D都市モデルの地形の表示ボタン
export function Func_chk_plateau_dem(map) {
  if (document.getElementById("ckb_plateau_dem").checked) {
    map.setTerrain({ source: "plateau_dem", exaggeration: 1 });
    //陰影を表示
    map.setLayoutProperty("plateau_hills", "visibility", "visible");
    //線状注記の調整（横置き）
    map.setLayoutProperty(
      "mapple_傾き部品注記",
      "text-rotation-alignment",
      "viewport"
    );
    map.setLayoutProperty(
      "mapple_傾き部品注記",
      "text-pitch-alignment",
      "auto"
    );
  } else {
    map.setTerrain(null);
    //陰影を非表示
    map.setLayoutProperty("plateau_hills", "visibility", "none");
    //線状注記の調整（傾き）
    map.setLayoutProperty(
      "mapple_傾き部品注記",
      "text-rotation-alignment",
      "map"
    );
    map.setLayoutProperty(
      "mapple_傾き部品注記",
      "text-pitch-alignment",
      "viewport"
    );
  }
}

//3D都市モデルの建物の表示ボタン
export function Func_chk_radio_plateau_building_nodisp(map) {
  if (document.getElementById("radio_plateau_building_nodisp").checked) {
    map.setLayoutProperty("plateau_bldg_fill_outline", "visibility", "none");
    map.setLayoutProperty("plateau_bldg_fill", "visibility", "none");
    map.setLayoutProperty("plateau_bldg", "visibility", "none");
  }
}
export function Func_chk_radio_plateau_building_2d(map) {
  if (document.getElementById("radio_plateau_building_2d").checked) {
    map.setLayoutProperty("plateau_bldg_fill_outline", "visibility", "visible");
    map.setLayoutProperty("plateau_bldg_fill", "visibility", "visible");
    map.setLayoutProperty("plateau_bldg", "visibility", "none");
  } else {
    map.setLayoutProperty("plateau_bldg_fill_outline", "visibility", "none");
    map.setLayoutProperty("plateau_bldg_fill", "visibility", "none");
  }
}
export function Func_chk_radio_plateau_building_3d(map) {
  if (document.getElementById("radio_plateau_building_3d").checked) {
    map.setLayoutProperty("plateau_bldg_fill_outline", "visibility", "none");
    map.setLayoutProperty("plateau_bldg_fill", "visibility", "none");
    map.setLayoutProperty("plateau_bldg", "visibility", "visible");
  } else {
    map.setLayoutProperty("plateau_bldg", "visibility", "none");
  }
}

export function Func_chk_PLATEAU_Building_Visibility(map) {
  Func_chk_radio_plateau_building_nodisp(map);
  Func_chk_radio_plateau_building_2d(map);
  Func_chk_radio_plateau_building_3d(map);
}

// サーバの時刻取得
export const getServerTime = async () => {
  // CloudFrontのキャッシュ問題のため、一時的にクライアント時刻とする
  return new Date();
  // const response = await fetch(window.location.href, {
  //   method: "HEAD",
  //   cache: "no-cache",
  // }).catch((err) => {
  //   console.error(err);
  // });
  // return response ? new Date(response.headers.get("Date")) : null;
};

// 利用規約に同意済みかチェックする
export const isLatestTermsConsented = (user) => {
  // 再同意が必要なときはfalseを返す
  // 同意日の値が存在しない、またはnullの場合のデフォルト値を設定
  const rawConsentDate = user?.attributes?.["custom:lastconsentdatetime"] || "2026-03-09";

  const userConsentDate = new Date(rawConsentDate);
  const updateDate = new Date(LATEST_TERMS_UPDATE);

  // 比較ロジック（最新の更新日よりも同意日が新しいか同じであれば true）
  return updateDate.getTime() <= userConsentDate.getTime();
};

// カラーコードの形式チェック
export const checkColor = (color) => {
  // colorのチェック（ハッシュ+16進数6文字）
  const colorRegex = /^#[0-9A-Fa-f]{6}$/;
  return colorRegex.test(color);
};

// 線幅の形式チェック
export const checkWidth = (width) => {
  return typeof width === "number" && width >= 0;
};

// 透明度の形式チェック
export const checkOpacity = (opacity) => {
  return typeof opacity === "number" && 0 <= opacity && opacity <= 1;
};

/**
 * iOSデバイス（iPhone, iPad）であるか判定します。
 * @returns {boolean} iOSデバイスであればtrue
 */
export const isIOS = () => {
  return /iPad|iPhone/.test(navigator.userAgent);
};

/**
 * iOSデバイス上のChromeブラウザであるか判定します。
 * @returns {boolean} iOS Chromeであればtrue
 */
export const isIOSChrome = () => {
  return /CriOS/.test(navigator.userAgent);
};

/**
 * iOSデバイス上のSafariブラウザであるか判定します。
 * @returns {boolean} iOS Safariであればtrue
 */
export const isIOSSafari = () => {
  // isIOS()は既存の関数
  return isIOS() && !isIOSChrome();
};

/**
 * Base64エンコードされた画像データをファイルとしてダウンロードします。
 * @param {string} base64Data - "data:image/png;base64,..."形式のBase64文字列
 * @param {string} fileName - ダウンロードするファイル名
 */
export const downloadImageFromBase64 = (base64Data, fileName) => {
  // Base64データからBlobを生成
  const byteString = atob(base64Data.split(",")[1]);
  const mimeString = base64Data.split(",")[0].split(":")[1].split(";")[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  const blob = new Blob([ab], { type: mimeString });

  // ダウンロードリンクを生成してクリック
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  document.body.appendChild(a);
  a.style.display = "none";
  a.href = url;
  a.download = fileName;
  a.click();

  // 後処理
  URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

// 全角→半角変換
export const toHalfWidth = (str) => {
  if (!str) return "";
  return str
    .replace(/ー/g, "-")
    .replace(/－/g, "-")
    .replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) =>
      String.fromCharCode(s.charCodeAt(0) - 0xfee0)
    );
};

let obj_ToukiCenterOutsideBusinessHours = [];
let isBusinessHoursDataFetched = false;

// 営業時間の臨時休業データをフェッチする
const fetchToukiCenterOutsideBusinessHours = async () => {
  if (isBusinessHoursDataFetched) return;
  try {
    const response = await fetch(
      "mapple01/mojmap/ToukiCenterOutsideBusinessHours.txt"
    );
    const data = await response.text();
    const lines = data.split(/\r\n/);
    obj_ToukiCenterOutsideBusinessHours = lines
      .map((line) => {
        const parts = line.split(" ");
        return {
          day: parts[0],
          start: parts[1],
          end: parts[2],
          message: parts[3],
        };
      })
      .filter((item) => item.day); // 空行を除外
    isBusinessHoursDataFetched = true;
  } catch (error) {
    console.error(
      "Failed to fetch ToukiCenterOutsideBusinessHours.txt:",
      error
    );
  }
};
await fetchToukiCenterOutsideBusinessHours();

// JSTで曜日・時刻を取得するフォーマッタ
const dateTimeFormatterJST_forIndex = new Intl.DateTimeFormat("ja-JP", {
  timeZone: "Asia/Tokyo",
  weekday: "short",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

// JSTで年月日を取得するフォーマッタ
const ymdFormatterJST_forIndex = new Intl.DateTimeFormat("ja-JP", {
  timeZone: "Asia/Tokyo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

function ISSameDay(date1, date2) {
  if (!date1 || !date2) return false;
  return (
    ymdFormatterJST_forIndex.format(date1) ===
    ymdFormatterJST_forIndex.format(date2)
  );
}

function ISInTime(serverDate, start, end) {
  if (!serverDate || serverDate.getFullYear() < 2000) {
    return false;
  }
  const parts = dateTimeFormatterJST_forIndex.formatToParts(serverDate);
  let hour, minute;
  for (const part of parts) {
    if (part.type === "hour") hour = part.value;
    if (part.type === "minute") minute = part.value;
  }
  const timeString = `${hour}${minute}`;
  return timeString >= start && timeString < end;
}

function IsRingiOutOfBusiness(serverDate) {
  let ret = "";
  obj_ToukiCenterOutsideBusinessHours.forEach(function (val) {
    if (ISSameDay(serverDate, new Date(val.day))) {
      if (ISInTime(serverDate, val.start, val.end)) {
        ret = val.message;
      }
    }
  });

  return ret;
}

export const checkBusinessHours = async () => {
  const serverDate = await getServerTime();
  if (!serverDate) {
    return {
      isBuyable: false,
      message: "システムエラーにより、購入できません",
    };
  }

  const ringiMessage = IsRingiOutOfBusiness(serverDate);
  if (ringiMessage) {
    return { isBuyable: false, message: ringiMessage };
  }
  const parts = dateTimeFormatterJST_forIndex.formatToParts(serverDate);
  const dayString = parts.find((part) => part.type === "weekday")?.value;
  let endTime;
  if (dayString === "日" || dayString === "土") {
    endTime = "1730";
  } else {
    endTime = "2230";
  }
  const startTime = "0830";
  if (ISInTime(serverDate, startTime, endTime)) {
    return {
      isBuyable: true,
      message: "不動産登記情報（土地）を購入（オンライン決済画面に移動）",
    };
  } else {
    return { isBuyable: false, message: "ただいまの時間は購入できません" };
  }
};

/**
 * 購入履歴のitemから複合キーを生成する
 * @param {object} item - 購入履歴のitem
 * @returns {string} 複合キー
 */
export const createOrderHistoryKey = (item) => {
  return `${item.prefcode || ""}-${item.city || ""}-${item.oaza || ""}-${
    item.chome || ""
  }-${item.koaza || ""}-${item.yobi || ""}-${toHalfWidth(item.chiban) || ""}`;
};

/**
 * 地図地物のプロパティから複合キーを生成する
 * @param {object} props - 地図地物のプロパティ
 * @returns {string} 複合キー
 */
export const createFeaturePropertyKey = (props) => {
  return `${(props.市区町村コード || "").substring(0, 2)}-${
    props.市区町村名 || ""
  }-${props.大字名 || ""}-${props.丁目名 || ""}-${props.小字名 || ""}-${
    props.予備名 || ""
  }-${toHalfWidth(props.地番) || ""}`;
};